import React, { useState, useMemo, useEffect } from 'react';
import { useProducts } from '../contexts/ProductContext';
import ProductCard from '../components/product/ProductCard';
import { getDisplayCategory } from '../types/product';
import {
  extractCategoriesFromVariants,
  extractGalwayCategories,
  getRawCategory,
} from '../utils/categoryUtils';
import { COLLECTIONS } from '../types/collection';
import './ShopPage.css';

interface CategoryShopPageProps {
  categoryFilter: string;
}

const CategoryShopPage: React.FC<CategoryShopPageProps> = ({ categoryFilter }) => {
  const { products, productVariants, loading, currentCollection, searchTerm, setSearchTerm } = useProducts();
  const [selectedCategory, setSelectedCategory] = useState(categoryFilter);

  const isGalwayCrystal = currentCollection === COLLECTIONS.GALWAY_CRYSTAL;

  // Set category filter on mount
  useEffect(() => {
    setSelectedCategory(categoryFilter);
    setSearchTerm('');
  }, [categoryFilter, setSearchTerm]);

  // Extract categories dynamically from products
  const categories = useMemo(() => {
    if (isGalwayCrystal) {
      return extractGalwayCategories(products);
    }
    return extractCategoriesFromVariants(productVariants);
  }, [products, productVariants, isGalwayCrystal]);

  // Filter products for Galway Crystal
  const filteredProducts = useMemo(() => {
    let filtered = products;

    // Filter by category (main or subcategory)
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(
        (p) =>
          p.category === selectedCategory ||
          (p.type && p.type === selectedCategory)
      );
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.name.toLowerCase().includes(term) ||
          p.description.toLowerCase().includes(term) ||
          p.category.toLowerCase().includes(term) ||
          (p.sku && p.sku.toLowerCase().includes(term)) ||
          (p.type && p.type.toLowerCase().includes(term))
      );
    }

    return filtered;
  }, [products, selectedCategory, searchTerm]);

  // Filter product variants for Trophies & Medals
  const filteredVariants = useMemo(() => {
    let filtered = productVariants;

    // Filter by category
    if (selectedCategory !== 'All') {
      const rawCategory = getRawCategory(selectedCategory);
      filtered = filtered.filter((v) =>
        getDisplayCategory(v.defaultVariant).toLowerCase() === rawCategory.toLowerCase()
      );
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((v) => {
        // Search in variant's base properties
        if (
          v.name.toLowerCase().includes(term) ||
          v.description.toLowerCase().includes(term) ||
          v.category.toLowerCase().includes(term)
        ) {
          return true;
        }

        // Search in any of the variant products
        return v.variants.some(
          (p) =>
            p.name.toLowerCase().includes(term) ||
            p.productId.toLowerCase().includes(term) ||
            (p.sku && p.sku.toLowerCase().includes(term))
        );
      });
    }

    return filtered;
  }, [productVariants, selectedCategory, searchTerm]);

  if (loading) {
    return (
      <div className="shop-page-loading">
        <div className="spinner"></div>
        <p>Loading products...</p>
      </div>
    );
  }

  return (
    <div className="shop-page">
      {/* Category Filter */}
      <div className="category-filter">
        {categories.map((category) => (
          <button
            key={category}
            className={`category-chip ${
              selectedCategory === category ? 'category-chip-active' : ''
            }`}
            onClick={() => setSelectedCategory(category)}
          >
            {category}
          </button>
        ))}
      </div>

      {/* Product Grid */}
      {isGalwayCrystal ? (
        // Galway Crystal products (no variants)
        filteredProducts.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">💎</div>
            <p>No products found</p>
          </div>
        ) : (
          <div className="product-grid">
            {filteredProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )
      ) : (
        // Trophies & Medals (with variants)
        filteredVariants.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🏆</div>
            <p>No products found</p>
          </div>
        ) : (
          <div className="product-grid">
            {filteredVariants.map((variant) => (
              <ProductCard key={variant.baseId} variant={variant} />
            ))}
          </div>
        )
      )}
    </div>
  );
};

export default CategoryShopPage;

